import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const TermsPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4">Legal</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-slate-900">
              Terms & <span className="text-primary">Conditions</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Please read these terms and conditions carefully before using our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-0 shadow-lg bg-slate-50/50 p-6 md:p-10">
              <CardContent className="prose prose-slate max-w-none space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Terms and Conditions</h2>
                  <p className="text-slate-600 leading-relaxed">
                    These Terms and Conditions govern your use of Caliber Nova's website, services, and educational programs. 
                    By accessing or using this website, you agree to comply with these terms.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Acceptance of Terms</h3>
                  <p className="text-slate-600 leading-relaxed">
                    By using this website, you agree to these Terms and Conditions. 
                    We reserve the right to update or modify these terms at any time without prior notice.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Business Identity</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Caliber Nova is an educational institute dedicated to providing quality coaching and academic support for students preparing for competitive and academic examinations.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Eligibility</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Students must provide accurate information during registration. If a student is under 18 years of age, parental or guardian consent may be required for enrollment.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Services Offered</h3>
                  <p className="text-slate-600 leading-relaxed">
                    We provide educational coaching and academic support, including but not limited to:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>Classroom Coaching</li>
                    <li>Offline Coaching</li>
                    <li>Study Materials</li>
                    <li>Test Series & Mock Exams</li>
                    <li>Academic Mentorship & Guidance</li>
                    <li>Career Counseling</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Communication & WhatsApp Usage</h3>
                  <p className="text-slate-600 leading-relaxed">
                    By submitting your contact details through our website, inquiry forms, or admission process, you consent to receive communication via WhatsApp, phone calls, SMS, or email regarding:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>Admission inquiries</li>
                    <li>Course-related updates</li>
                    <li>Academic information</li>
                    <li>Test schedules and notifications</li>
                    <li>Student support and assistance</li>
                  </ul>
                  <p className="text-slate-500 italic mt-2 text-sm">
                    *We do not send spam or unauthorized promotional messages.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Payments & Refund Policy</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Accepted payment modes may include UPI, bank transfer, debit/credit cards, online payment gateways, or cash.
                  </p>
                  <p className="text-slate-600 leading-relaxed mt-2 font-medium">
                    Refund Policy:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-1">
                    <li>Refund requests must be submitted within 7 days of enrollment.</li>
                    <li>Administrative and registration charges may be non-refundable.</li>
                    <li>No refunds will be applicable once classes have commenced.</li>
                    <li>Approved refunds will be processed within 10–15 business days.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Intellectual Property Rights</h3>
                  <p className="text-slate-600 leading-relaxed">
                    All website content, study materials, videos, lectures, test papers, logos, and branding are the intellectual property of Caliber Nova. 
                    Unauthorized copying, sharing, recording, reproduction, or distribution of any content is strictly prohibited.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Prohibited Activities</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Users and students must not:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>Share or resell study materials</li>
                    <li>Record or distribute lectures without permission</li>
                    <li>Share online learning credentials with others</li>
                    <li>Provide false information during registration</li>
                  </ul>
                  <p className="text-slate-500 italic mt-2 text-sm">
                    *Violations may lead to suspension or termination of services.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Limitation of Liability</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Caliber Nova provides coaching, guidance, and academic support to students. 
                    Academic performance and examination results depend on individual effort, preparation, and attendance. 
                    We do not guarantee admission, ranks, scores, scholarships, or success in any examination. 
                    Website availability may occasionally be affected due to technical reasons beyond our control.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Privacy & Data Protection</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Student and parent information is handled responsibly and used only for educational, administrative, and communication purposes in accordance with applicable Indian laws. 
                    Please refer to our Privacy Policy for more details.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Termination of Services</h3>
                  <p className="text-slate-600 leading-relaxed">
                    We reserve the right to suspend or terminate services for policy violations, non-payment of fees, misconduct, or misuse of institute resources.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Governing Law & Jurisdiction</h3>
                  <p className="text-slate-600 leading-relaxed">
                    These terms are governed by the laws of India, and any disputes shall be subject to the jurisdiction of the courts of Nagpur, Maharashtra.
                  </p>
                </div>

                <div className="pt-6 border-t border-slate-200">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Contact Information</h3>
                  <p className="text-slate-700 font-semibold">Caliber Nova</p>
                  <ul className="space-y-1 text-slate-600 text-sm mt-2">
                    <li><strong>Email:</strong> info@calibernova.in</li>
                    <li><strong>Website:</strong> www.calibernova.in</li>
                    <li><strong>Address:</strong> 2nd Floor, Zenda Chowk, Mahal, Nagpur - 440032</li>
                    <li><strong>Phone:</strong> +91 9595253778 | +91 9527225500</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsPage;
